import { useState } from "react";

const getPhotoUrl = (name: string) => {
  if (name.includes("CVS")) return "https://www.cvs.com/store-locator";
  if (name.includes("Walgreens")) return "https://www.walgreens.com/storelocator";
  if (name.includes("UPS Store")) return "https://www.theupsstore.com";
  if (name.includes("FedEx")) return "https://www.fedex.com";
  if (name.includes("Walmart")) return "https://www.walmart.com/store-finder";
  return "#";
};

const photoLocations = [
  { name: "CVS Pharmacy — Marietta", address: "1595 Cobb Parkway S, Marietta, GA 30060", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Marietta", address: "1750 Powder Springs Road, Marietta, GA 30064", hours: "Call for hours" },
  { name: "CVS Pharmacy — Smyrna", address: "4500 West Village Parkway, Smyrna, GA 30080", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Kennesaw", address: "3920 Cherokee Street, Kennesaw, GA 30144", hours: "Call for hours" },
  { name: "The UPS Store — Marietta", address: "1230 Johnson Ferry Road, Marietta, GA 30068", hours: "Call for hours" },
  { name: "FedEx Office — Marietta", address: "1605 Cobb Parkway S, Marietta, GA 30060", hours: "Call for hours" },
  { name: "Walmart Supercenter — Marietta", address: "1795 Cobb Parkway S, Marietta, GA 30060", hours: "Daily 6 AM – 11 PM" },
  { name: "CVS Pharmacy — Roswell", address: "1100 Old Alabama Road, Roswell, GA 30075", hours: "Daily 7 AM – 11 PM" },
];

const dmvLocations = [
  {
    name: "Georgia DDS — Marietta",
    address: "1605 Cobb Parkway S, Marietta, GA 30060",
    hours: "Tue–Sat, 8:00 AM – 5:00 PM",
    note: "REAL ID, driver licenses, and ID cards. Appointment recommended. Check DDS website for current wait times.",
    featured: true,
  },
  {
    name: "Georgia DDS — Kennesaw",
    address: "3699 Kennesaw Street, Kennesaw, GA 30144",
    hours: "Tue–Sat, 8:00 AM – 5:00 PM",
    note: "Northwest Cobb County option. Online appointment system available.",
    featured: false,
  },
];

export default function MariettaHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Marietta Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your expedited passport ready involves more than just the application. Here are local Cobb County and metro Atlanta resources to help you gather everything you need for your emergency passport.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in Marietta &amp; Cobb County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your expedited passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Marietta include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <a href={getPhotoUrl(loc.name)} target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] text-xs leading-snug mb-1 inline-flex items-center gap-1 hover:text-[#F6C344] transition-colors">{loc.name} <i className="ri-external-link-line text-[10px]"></i></a>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications for your expedited passport — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Georgia DDS — Marietta &amp; Cobb County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your expedited passport application. If you need a new or renewed Georgia driver&apos;s license or REAL ID, the Georgia Department of Driver Services (DDS) handles licensing in the Marietta area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <a href="https://dds.georgia.gov/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] text-xs leading-snug inline-flex items-center gap-1 hover:text-[#F6C344] transition-colors">{loc.name} <i className="ri-external-link-line text-[10px]"></i></a>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://dds.georgia.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Georgia DDS Official Website
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times at Georgia DDS offices can be long. Book your slot online at the Georgia DDS website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Marietta &amp; Cobb County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Marietta travelers primarily use Hartsfield-Jackson Atlanta International Airport. If your trip is coming up fast, make sure your expedited passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "ATL",
                    name: "Hartsfield-Jackson Atlanta International Airport",
                    distance: "Primary airport — about 25 miles southeast of Marietta",
                    address: "6000 N Terminal Pkwy, Atlanta, GA 30320",
                    note: "The world's busiest airport. Major hub for Delta, Southwest, American, United &amp; extensive international carriers.",
                    flightUrl: "https://www.atl.com/",
                    flightLabel: "ATL Airport Website",
                    featured: true,
                  },
                  {
                    code: "RYY",
                    name: "Cobb County International Airport — McCollum Field",
                    distance: "General aviation — about 5 miles west of downtown Marietta",
                    address: "1723 McCollum Pkwy NW, Kennesaw, GA 30144",
                    note: "General aviation and charter flights. No commercial passenger service.",
                    flightUrl: "https://www.cobbcounty.org/airport",
                    flightLabel: "McCollum Field Info",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights from ATL.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited passport processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}